import React, { ChangeEvent, useEffect, useState } from 'react'
import { composeId, convertToKebabCase } from '../../utils'
import { InputText, IconWrapper, Hint, Prefix, LabelWrapper } from './styles'
import { useIMask } from '../../utils/use-imask'
import FormControl from '../form-control'
import Icon from '../icon'
import IMask from 'imask'
import InputLabel from '../input-label'

type Props = {
  'data-qa'?: string
  autoFocus?: boolean
  disabled?: boolean
  hint?: string
  icon?: string
  iconPosition?: 'default' | 'left'
  id?: string
  label?: string
  showLabel?: boolean
  mask?: string | RegExp | IMask.AnyMaskedOptions
  maxLength?: number
  minLength?: number
  name?: string
  onBlur?: Function
  onChange?: Function
  onClickIcon?: Function
  placeholder?: string
  style?: object
  theme?: TTheme
  type?: TInputType
  value: TValue
}

const Input: React.FC<Props> = (props: Props): JSX.Element => {
  const EMPTY_VALUE = ''
  const [displayedValue, setDisplayedValue] = useState<TValue>(props.value)
  const [rawValue, setRawValue] = useState<TValue>(props.value)
  const dataQa = `input-${convertToKebabCase(props.label)}`
  const hasMask = shouldHasMask(props)
  const id = composeId(props.label)
  const isMoney = props.type === 'money'
  const isPassword = props?.type === 'password'
  const maskOptions: IMask.AnyMaskedOptions = composeMaskOptions(props)
  const prefixIconName = composeNameOfPrefixIcon(props)
  const { reference } = useIMask(maskOptions, {
    onAccept: (e) => {
      setRawValue(e?.target?.value)
      props.onChange(e?.target?.value)
    },
  })

  useEffect(() => {
    isMoney && updateMoneyDisplayValue()
  }, [props.value])

  useEffect(() => {
    if (props.value !== rawValue) setRawValue(props.value)
  }, [props.value])

  function updateMoneyDisplayValue() {
    if (!props.value) return setDisplayedValue(EMPTY_VALUE)
    setDisplayedValue(applyCurrencyMask(+props.value))
  }

  function handleMoneyChange(event: ChangeEvent<HTMLInputElement>) {
    if (event.target.value) {
      const floatValue: number = +event.target.value.replace(/\D/g, '') / 100
      const maskedValue = applyCurrencyMask(floatValue)
      setDisplayedValue(maskedValue)
      props.onChange && props.onChange(convertCurrencyToFloat(maskedValue))
    } else {
      setDisplayedValue(undefined)
      props.onChange(undefined)
    }
  }

  function handleOnBlur() {
    props.onBlur && props.onBlur(convertCurrencyToFloat(`${displayedValue}`))
  }

  const convertCurrencyToFloat = (eventValue: string) => {
    return Number(eventValue.replace(/\./g, '').replace(',', '.')).toFixed(2)
  }

  const inputDefaultOptions = {
    className: `${props.theme} ${
      (prefixIconName || props.iconPosition === 'left') && 'has-prefix'
    }`,
    'data-qa': props['data-qa'] || dataQa,
    id: id,
    name: props.name,
    onBlur: () => props.onBlur && props.onBlur(rawValue),
    value: rawValue || '',
    disabled: props.disabled,
    placeholder: props.placeholder,
    maxLength: props.maxLength,
    minLength: props.minLength,
    autoFocus: props.autoFocus,
    style: props.style,
  }

  return (
    <FormControl>
      {props.showLabel && <InputLabel htmlFor={id} text={props.label} />}

      <LabelWrapper>
        {prefixIconName && (
          <Prefix>
            <Icon
              name={prefixIconName}
              size="18"
              color="neutralColorLowMedium"
            />
          </Prefix>
        )}

        {!hasMask && (
          <InputText
            {...inputDefaultOptions}
            type={isPassword ? 'password' : 'text'}
            onChange={(e) => {
              setRawValue(e.target.value)
              props.onChange(e.target.value)
              isMoney && updateMoneyDisplayValue()
            }}
          />
        )}

        {hasMask && !isMoney && (
          <InputText {...inputDefaultOptions} ref={reference} />
        )}

        {hasMask && isMoney && (
          <InputText
            {...inputDefaultOptions}
            value={displayedValue}
            onChange={handleMoneyChange}
            onBlur={handleOnBlur}
          />
        )}

        <IconWrapper
          className={props.iconPosition === 'left' && 'icon-position-left'}
        >
          {['complete', 'negative'].includes(props.theme) && (
            <Icon
              name={props.theme === 'complete' ? 'check' : 'close'}
              color={
                props.theme === 'complete'
                  ? 'feedbackColorPositivePure'
                  : 'feedbackColorNegativePure'
              }
              data-qa={`${inputDefaultOptions['data-qa']}-theme-icon`}
            />
          )}

          {props.icon && (
            <Icon
              name={props.icon}
              onClick={props.onClickIcon}
              data-qa={`${inputDefaultOptions['data-qa']}-icon`}
            />
          )}
        </IconWrapper>
      </LabelWrapper>

      {props.hint && (
        <Hint
          className={`is-${props.theme}`}
          data-qa={`${inputDefaultOptions['data-qa']}-hint`}
        >
          {props.hint}
        </Hint>
      )}
    </FormControl>
  )
}

Input.defaultProps = {
  maxLength: 255,
  value: '',
  theme: 'enabled',
  type: 'text',
  onChange: () => {},
  autoFocus: false,
  showLabel: true,
}

export default Input

function composeNameOfPrefixIcon({ type }: Props): string {
  const options = {
    money: 'brl',
    percentage: 'percentage',
  }
  return options[type] || null
}

function composeMaskOptions({ type, mask }: Props): IMask.AnyMaskedOptions {
  const regexpNumerical = /^[0-9]+([\.\,]{0,1})[0-9]*$/g
  switch (type) {
    case 'alphanum':
      return { mask: /^[A-Za-z0-9]*$/g }
    case 'email':
      return { mask: /^[\w*\.\-]*[@*]?[\w*]*\.?[\w*]*[\.*[\w*]*]*$/g }
    case 'number':
      return { mask: regexpNumerical }
    case 'percentage':
      return { mask: regexpNumerical }
    case 'phone':
      return {
        mask: [
          {
            mask: '(00) 0000-0000',
          },
          {
            mask: '(00) 00000-0000',
          },
        ],
        dispatch: (appended, dynamicMasked) => {
          const number = (dynamicMasked?.value + appended).replace(/\D/g, '')
          if (number.length > 10) {
            return dynamicMasked.compiledMasks[1]
          }
          return dynamicMasked.compiledMasks[0]
        },
      }
    default:
      return mask && !!mask['mask']
        ? (mask as unknown as IMask.AnyMaskedOptions)
        : { mask: mask as unknown as RegExp }
  }
}

function shouldHasMask(props: Props) {
  return (
    ['alphanum', 'email', 'money', 'phone', 'number', 'percentage'].includes(
      props.type,
    ) || !!props.mask
  )
}

type TValue = string | number | null | undefined
export type TTheme = 'complete' | 'enabled' | 'negative'
export type TInputType =
  | 'alphanum'
  | 'email'
  | 'money'
  | 'number'
  | 'password'
  | 'percentage'
  | 'text'
  | 'phone'

export const applyCurrencyMask = (value: number) => {
  const toFixed: string = String(value.toFixed(2)).replace(/\D/g, '')
  const nextValue = (+toFixed / 100).toLocaleString('pt-BR', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })
  return nextValue
}
